import React, { useCallback, useEffect, useState, useContext } from 'react';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header';
import { Col, Row, Modal } from 'react-bootstrap';
import Card from 'components/inputs/Card';
import Button from 'components/inputs/Button';
import * as _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import moment from 'moment';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import DeleteModal from 'components/general/DeleteModal';
import Footer from 'components/general/Footer';
import { NavContext } from 'components/privateRoute/PrivateRoute';
import { UPDATE_STATUS_CODE, DELETE_STATUS_CODE } from 'components/common/constant';
import {
  FETCH_PRELOADED_CAMPAIGNS,
  DELETE_PRELOADED_CAMPAIGN,
  DISBURSE_PRELOADED_CAMPAIGN,
} from 'actions/preloadedCampaign';

const PreloadedCampaignList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { navigateTo } = useContext(NavContext);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [campaignId, setCampaignId] = useState('');
  const [disbursingId, setDisbursingId] = useState('');

  const campaigns = useSelector((state) => state.preloadedCampaign.preloadedCampaigns);
  const isLoader = useSelector((state) => state.preloadedCampaign.isLoading);
  const profileData = useSelector((state) => state.profile.userProfile);
  const currentUserName = _.get(profileData, 'name', '');

  const getAllCampaigns = useCallback((data = {}) => {
    dispatch({ type: FETCH_PRELOADED_CAMPAIGNS.REQUEST, payload: data });
  }, []);

  useEffect(() => {
    getAllCampaigns();
  }, []);

  const deleteCampaign = useCallback(() => {
    dispatch({
      type: DELETE_PRELOADED_CAMPAIGN.REQUEST,
      payload: campaignId,
      cb: (res) => {
        if (_.get(res, 'status') === DELETE_STATUS_CODE || _.get(res, 'status') === UPDATE_STATUS_CODE) {
          setShowDeleteModal(false);
          getAllCampaigns();
        }
      },
    });
  }, [campaignId]);

  const disburse = useCallback((id) => {
    setDisbursingId(id);
    dispatch({
      type: DISBURSE_PRELOADED_CAMPAIGN.REQUEST,
      payload: id,
      cb: (res) => {
        setDisbursingId('');
        const data = _.get(res, 'data');
        if (data && (data.credited >= 0)) {
          const parts = [`${data.credited} credited`, `${data.skipped} skipped`];
          if (data.skipped_active_elsewhere) parts.push(`${data.skipped_active_elsewhere} already in another campaign`);
          if (data.not_matched) parts.push(`${data.not_matched} not registered`);
          if (_.get(data, 'failed.length', 0)) parts.push(`${data.failed.length} failed`);
          toast.success(`Disbursed: ${parts.join(', ')}`);
          // Surface WHO was not disbursed (and why) so the admin can follow up.
          const nd = _.get(data, 'not_disbursed', []);
          if (nd.length) {
            const preview = nd.slice(0, 8).map((x) => `${x.phone || '—'} (${x.reason})`).join('; ');
            toast.warning(
              `${nd.length} user(s) not disbursed: ${preview}${nd.length > 8 ? ' …' : ''}`,
              { autoClose: 10000 }
            );
          }
          getAllCampaigns();
        }
      },
    });
  }, []);

  const fmt = (d) => (d ? moment(d).format('DD MMM YYYY') : '-');

  return (
    <React.Fragment>
      <LayoutContainer>
        <AdminHeader title={t('header.preloadedCampaigns', 'Pre-Loaded Campaigns')} />
        <SkeletonTheme baseColor="#1b2a38" highlightColor="#15212b" enableAnimation="true">
          <div className="page-content-wrapper scrollable">
            <div className="user-group-page_main">
              <div className="user-group-main-block">
                <Card>
                  <div className="user-group-block">
                    <div className="user-group-search-box">
                      <Row>
                        <Col xs={12} className="d-flex justify-content-end">
                          <Button className="user-group-btn" onClick={() => navigateTo('/addPreloadedCampaign')}>
                            {t('button.addCampaign', 'Add Campaign')}
                          </Button>
                        </Col>
                      </Row>
                    </div>
                    <div className="user-group-table">
                      <div className="table-responsive">
                        <table className="record-list-table">
                          <thead>
                            <tr>
                              <th>{t('preloadedCampaignList.name', 'Name')}</th>
                              <th>{t('preloadedCampaignList.amount', 'Amount')}</th>
                              <th>{t('preloadedCampaignList.n', 'Max Sessions (N)')}</th>
                              <th>{t('preloadedCampaignList.m', 'M %')}</th>
                              <th>{t('preloadedCampaignList.x', 'Min Units (X)')}</th>
                              <th>{t('preloadedCampaignList.window', 'Window')}</th>
                              <th>{t('preloadedCampaignList.state', 'Status')}</th>
                              <th>{t('preloadedCampaignList.disbursed', 'Disbursed')}</th>
                              <th className="action-col">{t('preloadedCampaignList.action', 'Action')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {isLoader ? (
                              <tr>
                                <td colSpan={9} className="border-0">
                                  <Skeleton height={15} />
                                </td>
                              </tr>
                            ) : _.isEmpty(campaigns) ? (
                              <tr>
                                <td colSpan={9} className="border-0">
                                  <div className="empty-data-block">
                                    {t('preloadedCampaignList.noCampaigns', 'No campaigns found')}
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              _.map(campaigns, (item, index) => (
                                <tr key={`preloaded-campaign-${index}`}>
                                  <td>{_.get(item, 'name', '')}</td>
                                  <td>{_.get(item, 'amount', 0)}</td>
                                  <td>{_.get(item, 'max_sessions', 0)}</td>
                                  <td>{_.get(item, 'percentage_per_session', 0)}</td>
                                  <td>{_.get(item, 'min_units', 0)}</td>
                                  <td>
                                    {fmt(_.get(item, 'start_date'))} - {fmt(_.get(item, 'end_date'))}
                                  </td>
                                  <td>
                                    {(() => {
                                      // Pause toggle removed (CDB-1286): a campaign
                                      // is live purely by its date window + status.
                                      if (_.get(item, 'status') === 'cancelled') return 'Cancelled';
                                      const now = moment();
                                      if (_.get(item, 'end_date') && now.isAfter(_.get(item, 'end_date'))) return 'Expired';
                                      if (_.get(item, 'start_date') && now.isBefore(_.get(item, 'start_date'))) return 'Scheduled';
                                      return 'Live';
                                    })()}
                                  </td>
                                  <td>{_.get(item, 'disbursed_user_count', 0)}</td>
                                  <td>
                                    <div className="d-flex" style={{ gap: 8 }}>
                                                                              <Button
                                          className="user-group-btn"
                                          onClick={() => navigateTo(`/editPreloadedCampaign/${_.get(item, 'id')}`)}
                                        >
                                          {t('button.edit', 'Edit')}
                                        </Button>
                                                                              <Button
                                          className="user-group-btn"
                                          disabled={disbursingId === _.get(item, 'id')}
                                          onClick={() => disburse(_.get(item, 'id'))}
                                        >
                                          {disbursingId === _.get(item, 'id')
                                            ? t('button.disbursing', 'Disbursing...')
                                            : t('button.disburse', 'Disburse')}
                                        </Button>
                                                                              <Button
                                          className="user-group-delete-Btn"
                                          onClick={() => {
                                            setShowDeleteModal(true);
                                            setCampaignId(_.get(item, 'id', ''));
                                          }}
                                        >
                                          {t('button.delete', 'Delete')}
                                        </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </SkeletonTheme>
        {showDeleteModal && (
          <Modal show={showDeleteModal} centered onHide={() => setShowDeleteModal(false)}>
            <DeleteModal title="Campaign" onClose={() => setShowDeleteModal(false)} onRemove={deleteCampaign} />
          </Modal>
        )}
        {currentUserName === 'CZ Tenant Admin' ? null : <Footer />}
      </LayoutContainer>
    </React.Fragment>
  );
};

export default PreloadedCampaignList;
